import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type Stripe from 'stripe';
import { getBytesFromGigabytes } from '../../../billing-consts';
import { DataUnitsTransformBigInt } from '../../../lib/data-units';
import { getMetadataField } from '../../../lib/get-metadata-field';
import { PermissionService } from '../../../services/permission.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';

@Component({
  selector: 'app-checkout-plan-change-box',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './checkout-plan-change-box.component.html',
  styleUrl: './checkout-plan-change-box.component.scss',
  imports: [SvgComponent],
})
export class CheckoutPlanChangeBoxComponent {
  private permissionService = inject(PermissionService);
  private subscriptionService = inject(SubscriptionService);
  mainPrice = toSignal(this.subscriptionService.getMainPrice());
  price = input.required<Stripe.Price & { product: Stripe.Product }>();

  lines = computed(() => {
    const mainPrice = this.mainPrice();
    const newPrice = this.price();
    if (!mainPrice || !newPrice) return [];
    const lines = [
      this.getIncludedSeatsLine(mainPrice, newPrice),
      this.getStorageLine(mainPrice, newPrice),
      this.getFeaturesLine(newPrice),
    ];
    return lines;
  });

  private getIncludedSeatsLine(
    currentPrice: Stripe.Price & {
      product: Stripe.Product;
    },
    newPrice: Stripe.Price & {
      product: Stripe.Product;
    }
  ) {
    const currentSeatCount = Number(getMetadataField(currentPrice, 'included_seat_count'));
    const newSeatCount = Number(getMetadataField(newPrice, 'included_seat_count'));

    return {
      from: this.getIncludedSeatsLineText(currentSeatCount),
      to: this.getIncludedSeatsLineText(newSeatCount),
      type: currentSeatCount === newSeatCount ? 'same' : currentSeatCount < newSeatCount ? 'up' : 'down',
    };
  }

  private getIncludedSeatsLineText(seats: number) {
    return `${seats}x included seat`;
  }

  private getStorageLine(
    currentPrice: Stripe.Price & {
      product: Stripe.Product;
    },
    newPrice: Stripe.Price & {
      product: Stripe.Product;
    }
  ) {
    const currentStorage = getBytesFromGigabytes(BigInt(getMetadataField(currentPrice, 'storage_capacity')));
    const newStorage = getBytesFromGigabytes(BigInt(getMetadataField(newPrice, 'storage_capacity')));

    return {
      from: this.getStorageLineText(currentStorage),
      to: this.getStorageLineText(newStorage),
      type: currentStorage === newStorage ? 'same' : currentStorage < newStorage ? 'up' : 'down',
    };
  }

  private getStorageLineText(storage: bigint) {
    return `${DataUnitsTransformBigInt(storage)} storage`;
  }

  private getFeaturesLine(
    newPrice: Stripe.Price & {
      product: Stripe.Product;
    }
  ) {
    const currentFeaturesCount = this.permissionService.getBillingScopes()?.length || 0;
    const productSlug = newPrice.product.name.toLowerCase().split(' ')[1];
    const newFeaturesCount = this.permissionService.getBillingScopesForProduct(productSlug)?.length || 0;
    return {
      from: this.getFeaturesLineText(currentFeaturesCount),
      to: this.getFeaturesLineText(newFeaturesCount),
      type:
        currentFeaturesCount === newFeaturesCount ? 'same' : currentFeaturesCount < newFeaturesCount ? 'up' : 'down',
    };
  }

  private getFeaturesLineText(features: number) {
    return `${features} features`;
  }
}
